import { DebugLogger } from '@affine/debug';
import { GraphQLError } from '@affine/error';
import type { ExecutionResult } from 'graphql';
import { isNil, isObject, merge } from 'lodash-es';

import type {
  GraphQLQuery,
  GraphQLUploadPlan,
  GraphQLUploadType,
} from './graphql';
import { prepareRequestJSON, type RequestPreparation } from './request-json';
import type { Mutations, Queries } from './schema';

export type NotArray<T> = T extends Array<unknown> ? never : T;

export type FetchInit = RequestInit & { timeout?: number };

export type _QueryVariables<Q extends GraphQLQuery> =
  Q['id'] extends Queries['name']
    ? Extract<Queries, { name: Q['id'] }>['variables']
    : Q['id'] extends Mutations['name']
      ? Extract<Mutations, { name: Q['id'] }>['variables']
      : undefined;

export type QueryVariables<Q extends GraphQLQuery> =
  _QueryVariables<Q> extends never | Record<string, never>
    ? never
    : _QueryVariables<Q>;

export type QueryResponse<Q extends GraphQLQuery> = Extract<
  Queries | Mutations,
  { name: Q['id'] }
>['response'];

type NullableKeys<T> = {
  [K in keyof T]: null extends T[K] ? K : never;
}[keyof T];

type NonNullableKeys<T> = {
  [K in keyof T]: null extends T[K] ? never : K;
}[keyof T];

export type RecursiveMaybeFields<T> = T extends
  | number
  | boolean
  | string
  | null
  | undefined
  ? T
  : {
      [K in NullableKeys<T>]?: RecursiveMaybeFields<T[K]>;
    } & {
      [K in NonNullableKeys<T>]: RecursiveMaybeFields<T[K]>;
    };

type AllowedRequestContext = Omit<RequestInit, 'method' | 'body'>;

export interface RequestBody {
  operationName?: string;
  variables: any;
  query: string;
  form?: FormData;
}

type QueryVariablesOption<Q extends GraphQLQuery> =
  QueryVariables<Q> extends never
    ? {
        variables?: undefined;
      }
    : { variables: RecursiveMaybeFields<QueryVariables<Q>> };

export type RequestOptions<Q extends GraphQLQuery> = QueryVariablesOption<Q> & {
  /**
   * parameter passed to `fetch` function
   */
  context?: AllowedRequestContext;
  /**
   * Whether keep null or undefined value in variables.
   *
   * if `false` given, `{ a: 0, b: undefined, c: null }` will be converted to `{ a: 0 }`
   *
   * @default true
   */
  keepNilVariables?: boolean;
  /**
   * Request timeout in milliseconds
   * @default 15000
   */
  timeout?: number;
  /**
   * Abort signal
   */
  signal?: AbortSignal;
  /** Native context owner checked across request preparation and before fetch. */
  assertContextCurrent?: () => void;
};

export type QueryOptions<Q extends GraphQLQuery> = RequestOptions<Q> & {
  query: Q;
};
export type MutationOptions<M extends GraphQLQuery> = RequestOptions<M> & {
  mutation: M;
};

function filterEmptyValue(vars: any) {
  const newVars: Record<string, any> = {};
  Object.entries(vars).forEach(([key, value]) => {
    if (isNil(value)) {
      return;
    }
    if (isObject(value) && !(value instanceof File)) {
      newVars[key] = filterEmptyValue(value);
      return;
    }
    newVars[key] = value;
  });

  return newVars;
}

interface UploadValue {
  path: string;
  value: unknown;
  descriptor: GraphQLUploadType;
}

function* uploadFields(
  value: object,
  fields: Record<string, GraphQLUploadType>,
  path: string
): Generator<UploadValue> {
  const record = value as Record<string, unknown>;
  for (const [name, descriptor] of Object.entries(fields)) {
    if (Object.hasOwn(record, name)) {
      yield { path: `${path}.${name}`, value: record[name], descriptor };
    }
  }
}

function* uploadChildren(
  entry: UploadValue,
  plan: GraphQLUploadPlan
): Generator<UploadValue> {
  const { value, path, descriptor } = entry;
  if (descriptor.listDepth > 0 && Array.isArray(value)) {
    const child = { ...descriptor, listDepth: descriptor.listDepth - 1 };
    for (let index = 0; index < value.length; index++) {
      if (Object.hasOwn(value, index)) {
        yield {
          path: `${path}.${index}`,
          value: value[index],
          descriptor: child,
        };
      }
    }
  } else if (value !== null && typeof value === 'object') {
    // GraphQL also coerces singleton values into list inputs.
    const fields = plan.inputs[descriptor.type];
    if (fields) yield* uploadFields(value, fields, path);
  }
}

export async function transformToForm(
  body: RequestBody,
  uploads: GraphQLUploadPlan,
  preparation: RequestPreparation = {}
) {
  preparation.signal?.throwIfAborted();
  preparation.assertContextCurrent?.();
  const form = new FormData();
  const gqlBody: {
    name?: string;
    query: string;
    variables: any;
    map: any;
  } = {
    query: body.query,
    variables: body.variables,
    map: {},
  };

  if (body.operationName) {
    gqlBody.name = body.operationName;
  }
  const map: Record<string, string[]> = {};
  const files: File[] = [];
  if (body.variables) {
    const variables: unknown = body.variables;
    if (variables === null || typeof variables !== 'object')
      throw new TypeError('Invalid upload variables');
    const ancestors = new WeakSet<object>();
    const stack = [
      {
        value: variables,
        iterator: uploadFields(variables, uploads.variables, 'variables'),
      },
    ];
    ancestors.add(variables);
    while (stack.length) {
      const frame = stack[stack.length - 1];
      const next = frame.iterator.next();
      if (next.done) {
        ancestors.delete(frame.value);
        stack.pop();
        continue;
      }
      const entry = next.value;
      if (entry.descriptor.type === 'Upload' && entry.value instanceof File) {
        map[String(files.length)] = [entry.path];
        files.push(entry.value);
      } else if (entry.value !== null && typeof entry.value === 'object') {
        if (ancestors.has(entry.value))
          throw new TypeError('Circular upload input');
        ancestors.add(entry.value);
        stack.push({
          value: entry.value,
          iterator: uploadChildren(entry, uploads),
        });
      }
    }
  }

  form.set('operations', (await prepareRequestJSON(gqlBody, preparation))!);
  form.set('map', JSON.stringify(map));
  for (const [i, file] of files.entries()) {
    form.set(`${i}`, file);
  }
  return form;
}

async function formatRequestBody<Q extends GraphQLQuery>({
  query,
  variables,
  keepNilVariables,
  signal,
  assertContextCurrent,
}: QueryOptions<Q>): Promise<string | FormData> {
  const body: RequestBody = {
    query: query.query,
    variables:
      (keepNilVariables ?? true) ? variables : filterEmptyValue(variables),
  };

  if (query.op) {
    body.operationName = query.op;
  }

  if (query.file) {
    if (!query.uploads) throw new TypeError('Missing generated upload plan');
    return transformToForm(body, query.uploads, {
      signal,
      assertContextCurrent,
    });
  }
  return (await prepareRequestJSON(body, { signal, assertContextCurrent }))!;
}

export const gqlFetcherFactory = (
  endpoint: string,
  fetcher: (input: string, init?: FetchInit) => Promise<Response> = fetch
) => {
  const logger = new DebugLogger('GraphQL');
  const gqlFetch = async <Query extends GraphQLQuery>(
    options: QueryOptions<Query>
  ): Promise<QueryResponse<Query>> => {
    // Avoid multipart traversal and JSON work for a request already stopped.
    if (options.signal?.aborted) throw options.signal.reason;
    if (
      (BUILD_CONFIG.appBuildType === 'canary' ||
        BUILD_CONFIG.appBuildType === 'dev') &&
      options.query.deprecations?.length
    ) {
      options.query.deprecations.forEach(deprecation => {
        logger.warn(deprecation);
      });
    }

    const body = await formatRequestBody(options);

    const isFormData = body instanceof FormData;
    const headers: Record<string, string> = {
      'x-operation-name': options.query.op,
      // SEC-001.4.2: CSRF protection - forces preflight request
      'apollo-require-preflight': 'true',
    };
    if (!isFormData) {
      headers['content-type'] = 'application/json';
    }
    options.signal?.throwIfAborted();
    options.assertContextCurrent?.();
    const ret = fetcher(
      endpoint,
      merge(options.context, {
        method: 'POST',
        headers,
        body,
        timeout: options.timeout,
        signal: options.signal,
      })
    ).then(async res => {
      if (res.headers.get('content-type')?.startsWith('application/json')) {
        const result = (await res.json()) as ExecutionResult;
        if (res.status >= 400 || result.errors) {
          if (result.errors && result.errors.length > 0) {
            // throw the first error is enough
            const firstError = result.errors[0];
            throw new GraphQLError(firstError.message, firstError);
          } else {
            throw new GraphQLError('Empty GraphQL error body');
          }
        } else if (result.data) {
          // we have to cast here because the type of result.data is a union type
          return result.data as any;
        }
      }

      throw new GraphQLError(
        'GraphQL query responds unexpected result, query ' + options.query.op
      );
    });

    return ret;
  };

  return gqlFetch;
};
